import React, { useState, useRef, useCallback, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChatInput.module.css';

interface ChatInputProps {
  onSend: (message: string) => void;
  onInterrupt?: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onInterrupt,
  isStreaming,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled) {
      onSend(trimmedMessage);
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [message, disabled, onSend]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStreaming && onInterrupt) {
        onInterrupt();
      } else {
        handleSend();
      }
    }
  }, [handleSend, isStreaming, onInterrupt]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }, []);

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('tutor.input_placeholder')}
          aria-label={t('companion.composer_label')}
          disabled={disabled && !isStreaming}
          rows={1}
        />

        <div className={styles.actions}>
          {isStreaming ? (
            <button
              className={`${styles.button} ${styles.interruptButton}`}
              onClick={onInterrupt}
              title={t('tutor.stop_enter')}
              aria-label={t('tutor.stop')}
            >
              ⏹
            </button>
          ) : (
            <button
              className={`${styles.button} ${styles.sendButton}`}
              onClick={handleSend}
              disabled={!message.trim() || disabled}
              title={t('tutor.send_enter')}
              aria-label={t('tutor.send')}
            >
              ➤
            </button>
          )}
        </div>
      </div>

      <div className={styles.hints}>
        <span>{t('tutor.input_hint')}</span>
      </div>
    </div>
  );
};

export default ChatInput;
